import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import Panel from "../components/Panel";
import {
    LineChart, Line, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ResponsiveContainer,
    Area, AreaChart, Sankey, Rectangle, Layer
} from "recharts";
import { BarChart3, GitBranch, TrendingUp, AlertTriangle, Sparkles } from "lucide-react";

const fmt = (v) => {
    if (v === null || v === undefined) return "—";
    const n = Number(v);
    if (Math.abs(n) >= 10000000) return `₹${(n / 10000000).toFixed(2)} Cr`;
    if (Math.abs(n) >= 100000) return `₹${(n / 100000).toFixed(2)} L`;
    return `₹${Math.round(n).toLocaleString("en-IN")}`;
};

// Custom SVG Sankey node renderer to match Bloomberg-terminal aesthetic
const SankeyNode = ({ x, y, width, height, index, payload, containerWidth }) => {
    const isSource = payload.kind === "source";
    const isHub = payload.kind === "hub";
    const color = isHub ? "#fb8b1e" : isSource ? "#4af6c3" : "#c084fc";
    const labelX = isSource ? x - 6 : x + width + 6;
    const anchor = isSource ? "end" : "start";
    return (
        <Layer key={`node-${index}`}>
            <Rectangle x={x} y={y} width={width} height={height} fill={color} fillOpacity={0.85} />
            <text
                textAnchor={anchor}
                x={labelX}
                y={y + height / 2 + 4}
                fontSize="11"
                fill="#e5e7eb"
                fontFamily="'IBM Plex Mono', monospace"
            >
                {payload.name}
            </text>
        </Layer>
    );
};

const SankeyLink = ({ sourceX, sourceY, sourceControlX, targetX, targetY, targetControlX, linkWidth, index }) => (
    <path
        d={`M${sourceX},${sourceY} C${sourceControlX},${sourceY} ${targetControlX},${targetY} ${targetX},${targetY}`}
        fill="none"
        stroke="#fb8b1e"
        strokeWidth={linkWidth}
        strokeOpacity={0.15}
    />
);

function SankeySection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/analytics/sankey").then((r) => { setData(r.data); setLoading(false); });
    }, []);

    if (loading) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Loading cash-flow map…</div>;
    if (!data) return null;

    return (
        <div className="space-y-3" data-testid="sankey-panel">
            <div className="grid grid-cols-3 gap-3">
                <Panel title="Total Inflow" subtitle="Realised">
                    <div className="text-2xl font-display font-black text-[var(--pos)]">{fmt(data.summary.total_inflow)}</div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">from {data.summary.sources} sources</div>
                </Panel>
                <Panel title="Total Outflow" subtitle="Applied">
                    <div className="text-2xl font-display font-black text-[var(--neg)]">{fmt(data.summary.total_outflow)}</div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">across {data.summary.uses} categories</div>
                </Panel>
                <Panel title="Net Position" subtitle="Inflow − Outflow">
                    <div className={`text-2xl font-display font-black ${data.summary.net >= 0 ? "text-[var(--pos)]" : "text-[var(--neg)]"}`}>{fmt(data.summary.net)}</div>
                    <div className="text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)] mt-1">operating cash margin</div>
                </Panel>
            </div>

            <Panel title="Cash Flow Sankey" subtitle="Sources → Cash & Bank Hub → Uses">
                <div style={{ width: "100%", height: 520 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <Sankey
                            data={data}
                            node={<SankeyNode />}
                            link={<SankeyLink />}
                            nodePadding={16}
                            nodeWidth={12}
                            margin={{ top: 20, bottom: 20, left: 140, right: 180 }}
                        >
                            <Tooltip
                                formatter={(v) => fmt(v)}
                                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 11, fontFamily: "'IBM Plex Mono', monospace" }}
                            />
                        </Sankey>
                    </ResponsiveContainer>
                </div>
            </Panel>
        </div>
    );
}

function ForecastSection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/analytics/forecast").then((r) => { setData(r.data); setLoading(false); });
    }, []);

    if (loading) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Fitting ARIMA model…</div>;
    if (!data) return null;

    // Build chart series: for each historical month push {label, actual}; for each forward month push {label, p10, p50, p90}
    const revenueSeries = [
        ...data.historical.months.map((m, i) => ({ label: m, actual_rev: data.historical.revenue[i], actual_col: data.historical.collections[i] })),
        ...data.forecast.months.map((m, i) => ({
            label: m,
            p10_rev: data.forecast.revenue.p10[i],
            p50_rev: data.forecast.revenue.p50[i],
            p90_rev: data.forecast.revenue.p90[i],
            p10_col: data.forecast.collections.p10[i],
            p50_col: data.forecast.collections.p50[i],
            p90_col: data.forecast.collections.p90[i],
        })),
    ];

    return (
        <div className="space-y-3" data-testid="forecast-panel">
            <Panel title="90-Day ARIMA Forecast" subtitle={data.model}>
                <div className="text-xs text-[var(--text-primary)]/80 font-body mb-2">{data.commentary}</div>
                <div style={{ width: "100%", height: 340 }}>
                    <ResponsiveContainer>
                        <AreaChart data={revenueSeries}>
                            <CartesianGrid stroke="#E2E8F0" strokeDasharray="2 4" />
                            <XAxis dataKey="label" tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} />
                            <YAxis tick={{ fill: "#9CA3AF", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }}
                                   tickFormatter={(v) => `${(v / 100000).toFixed(0)}L`} />
                            <Tooltip
                                formatter={(v) => fmt(v)}
                                contentStyle={{ background: "#FFFFFF", border: "1px solid #E2E8F0", fontSize: 11 }}
                            />
                            <Legend wrapperStyle={{ fontSize: 10, fontFamily: "'IBM Plex Mono', monospace" }} />
                            {/* Historical actuals */}
                            <Line type="monotone" dataKey="actual_rev" stroke="#fb8b1e" strokeWidth={2} dot={false} name="Actual Revenue" />
                            <Line type="monotone" dataKey="actual_col" stroke="#4af6c3" strokeWidth={2} dot={false} name="Actual Collections" />
                            {/* Forecast bands (revenue) */}
                            <Area type="monotone" dataKey="p90_rev" stroke="none" fill="#fb8b1e" fillOpacity={0.10} name="Revenue P90 (optimistic)" />
                            <Area type="monotone" dataKey="p10_rev" stroke="none" fill="#000" fillOpacity={0.0} legendType="none" />
                            <Line type="monotone" dataKey="p50_rev" stroke="#fb8b1e" strokeDasharray="4 3" strokeWidth={2} dot={false} name="Revenue P50 forecast" />
                            <Line type="monotone" dataKey="p10_rev" stroke="#dc2626" strokeDasharray="2 3" strokeWidth={1} dot={false} name="Revenue P10 (pessimistic)" />
                            <Line type="monotone" dataKey="p90_rev" stroke="#4af6c3" strokeDasharray="2 3" strokeWidth={1} dot={false} name="Revenue P90 (optimistic)" />
                        </AreaChart>
                    </ResponsiveContainer>
                </div>
            </Panel>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <Panel title="Revenue P50 · 6M" subtitle="Base case sum">
                    <div className="text-2xl font-display font-black text-[var(--brand-amber)]">
                        {fmt(data.forecast.revenue.p50.reduce((s, v) => s + v, 0))}
                    </div>
                </Panel>
                <Panel title="Revenue P10 · 6M" subtitle="Pessimistic sum">
                    <div className="text-2xl font-display font-black text-[var(--neg)]">
                        {fmt(data.forecast.revenue.p10.reduce((s, v) => s + v, 0))}
                    </div>
                </Panel>
                <Panel title="Revenue P90 · 6M" subtitle="Optimistic sum">
                    <div className="text-2xl font-display font-black text-[var(--pos)]">
                        {fmt(data.forecast.revenue.p90.reduce((s, v) => s + v, 0))}
                    </div>
                </Panel>
            </div>
        </div>
    );
}

function AnomalySection() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get("/analytics/anomalies").then((r) => { setData(r.data); setLoading(false); });
    }, []);

    if (loading) return <div className="text-xs font-mono-fin text-[var(--text-secondary)] p-6">Running anomaly checks…</div>;
    if (!data) return null;

    return (
        <div className="space-y-3" data-testid="anomaly-panel">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Panel title="At-Risk Estimate" subtitle="Composite">
                    <div className="text-2xl font-display font-black text-[var(--neg)]">{fmt(data.summary.total_at_risk_estimate)}</div>
                </Panel>
                <Panel title="Flagged Txns" subtitle="Across 5 checks">
                    <div className="text-2xl font-display font-black text-[var(--text-primary)]">{data.summary.flagged_txns}</div>
                </Panel>
                <Panel title="Benford Verdict" subtitle={`χ² = ${data.benford_first_digit.chi_squared}`}>
                    <div className={`text-lg font-display font-black ${data.benford_first_digit.verdict === "Suspicious" ? "text-[var(--neg)]" : data.benford_first_digit.verdict === "Acceptable" ? "text-[var(--brand-amber)]" : "text-[var(--pos)]"}`}>
                        {data.benford_first_digit.verdict}
                    </div>
                </Panel>
                <Panel title="Duplicates" subtitle="Pairs found">
                    <div className="text-2xl font-display font-black text-[var(--text-primary)]">{data.duplicates.count}</div>
                </Panel>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                <Panel title="Z-Score Outliers" subtitle={`${data.z_outliers.count} txns >2σ from mean`}>
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-1.5">Date</th>
                                <th className="text-left">Party</th>
                                <th className="text-right">Amount</th>
                                <th className="text-right">Z</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.z_outliers.top.map((t, i) => (
                                <tr key={i} className="border-b border-panel/60" data-testid={`z-outlier-${i}`}>
                                    <td className="py-1.5">{t.date}</td>
                                    <td className="truncate max-w-[180px]">{t.party}</td>
                                    <td className="text-right font-mono-fin">{fmt(t.amount)}</td>
                                    <td className="text-right font-mono-fin text-[var(--brand-amber)]">{t.z_score.toFixed(2)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Panel>

                <Panel title="Round-Amount Payments" subtitle={`${data.round_amounts.count} exact-lakh txns`}>
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-1.5">Date</th>
                                <th className="text-left">Party</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.round_amounts.items.map((t, i) => (
                                <tr key={i} className="border-b border-panel/60" data-testid={`round-amt-${i}`}>
                                    <td className="py-1.5">{t.date}</td>
                                    <td className="truncate max-w-[180px]">{t.party}</td>
                                    <td className="text-right font-mono-fin">{fmt(t.amount)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </Panel>

                <Panel title="Weekend Transactions" subtitle={`${data.weekend_txns.count} Sat/Sun txns`}>
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-1.5">Date</th>
                                <th className="text-left">Party</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.weekend_txns.items.map((t, i) => (
                                <tr key={i} className="border-b border-panel/60" data-testid={`weekend-${i}`}>
                                    <td className="py-1.5">{t.date}</td>
                                    <td className="truncate max-w-[180px]">{t.party}</td>
                                    <td className="text-right font-mono-fin">{fmt(t.amount)}</td>
                                </tr>
                            ))}
                            {data.weekend_txns.items.length === 0 && (
                                <tr><td colSpan="3" className="text-[var(--pos)] py-2 text-center">No weekend txns detected.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Panel>

                <Panel title="Suspected Duplicates" subtitle={`${data.duplicates.count} pairs (same vendor + amount)`}>
                    <table className="w-full text-xs font-body">
                        <thead>
                            <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                                <th className="text-left py-1.5">1st Invoice</th>
                                <th className="text-left">2nd Invoice</th>
                                <th className="text-right">Amount</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.duplicates.items.map((d, i) => (
                                <tr key={i} className="border-b border-panel/60" data-testid={`dup-${i}`}>
                                    <td className="py-1.5">{d.first_invoice}</td>
                                    <td>{d.second_invoice}</td>
                                    <td className="text-right font-mono-fin text-[var(--neg)]">{fmt(d.amount)}</td>
                                </tr>
                            ))}
                            {data.duplicates.items.length === 0 && (
                                <tr><td colSpan="3" className="text-[var(--pos)] py-2 text-center">No duplicates detected.</td></tr>
                            )}
                        </tbody>
                    </table>
                </Panel>
            </div>

            <Panel title="Benford Law · First-Digit Distribution" subtitle="Observed vs Expected (%)">
                <table className="w-full text-xs font-body">
                    <thead>
                        <tr className="border-b border-panel text-[10px] font-mono-fin uppercase tracking-wider text-[var(--text-secondary)]">
                            <th className="text-left py-1.5">Digit</th>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (<th key={d} className="text-right">{d}</th>))}
                        </tr>
                    </thead>
                    <tbody>
                        <tr className="border-b border-panel/60">
                            <td className="py-1.5 text-[var(--text-secondary)]">Expected</td>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => (
                                <td key={d} className="text-right font-mono-fin">{data.benford_first_digit.expected_pct[d]}</td>
                            ))}
                        </tr>
                        <tr>
                            <td className="py-1.5 text-[var(--text-primary)]">Observed</td>
                            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((d) => {
                                const o = data.benford_first_digit.observed_pct[d] || 0;
                                const e = data.benford_first_digit.expected_pct[d];
                                const diff = Math.abs(o - e);
                                return (
                                    <td key={d} className={`text-right font-mono-fin ${diff > 8 ? "text-[var(--neg)]" : diff > 4 ? "text-[var(--brand-amber)]" : "text-[var(--pos)]"}`}>{o}</td>
                                );
                            })}
                        </tr>
                    </tbody>
                </table>
            </Panel>
        </div>
    );
}

export default function Analytics() {
    const [tab, setTab] = useState("sankey");

    const tabs = [
        { key: "sankey", label: "Cash-Flow Sankey", icon: GitBranch },
        { key: "forecast", label: "ARIMA Forecast", icon: TrendingUp },
        { key: "anomaly", label: "Anomaly Detection", icon: AlertTriangle },
    ];

    return (
        <div className="space-y-4">
            <div>
                <div className="font-mono-fin text-[10px] uppercase tracking-[0.25em] text-[var(--brand-amber)] mb-1">// PREDICTIVE ANALYTICS</div>
                <h1 className="font-display font-black text-3xl flex items-center gap-3">
                    <BarChart3 className="text-[var(--brand-amber)]" size={26} />
                    Analytics & Forecast Lab
                </h1>
                <p className="mt-1 font-mono-fin text-[11px] text-[var(--text-secondary)] uppercase tracking-wider">
                    Sankey cash flow · ARIMA 6-month projection · statistical anomaly detection
                </p>
            </div>

            <div className="flex gap-1 border border-panel bg-panel p-1 w-fit" data-testid="analytics-tabs">
                {tabs.map((t) => {
                    const Icon = t.icon;
                    const active = tab === t.key;
                    return (
                        <button
                            key={t.key}
                            data-testid={`tab-${t.key}`}
                            onClick={() => setTab(t.key)}
                            className={`flex items-center gap-2 px-3 py-1.5 text-[11px] font-mono-fin uppercase tracking-wider transition-colors ${active ? "bg-[var(--brand-amber)] text-black" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`}
                        >
                            <Icon size={12} /> {t.label}
                        </button>
                    );
                })}
            </div>

            {tab === "sankey" && <SankeySection />}
            {tab === "forecast" && <ForecastSection />}
            {tab === "anomaly" && <AnomalySection />}
        </div>
    );
}
